import React, { useState, useEffect, useCallback } from 'react';
import { 
    FileText, CheckCircle, AlertTriangle, Search, DollarSign, 
    ChevronDown, ChevronUp, CreditCard, CheckCircle2 
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/Sidebar';
import API from '../../services/api';
import '../Dashboard.css';

const HistoriqueExtras = () => {
  const [consommations, setConsommations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filtreStatut, setFiltreStatut] = useState('TOUS'); // 'TOUS', 'SOLDE', 'IMPAYE'

  // --- ÉTAT DU FORMULAIRE DE PAIEMENT DÉPLIÉ PAR LIGNE ---
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [montantVerse, setMontantVerse] = useState('');
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('');
  const [referencePaiement, setReferencePaiement] = useState('');
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = useCallback((message, type = 'success') => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [resConso, resMethods] = await Promise.all([
        API.get('/hebergement/extras/historique').catch(() => ({ data: [] })),
        API.get('/plan-comptable/paiements/methodes').catch(() => ({ data: { success: false, data: [] } }))
      ]);

      const raw = resConso.data;
      setConsommations(Array.isArray(raw) ? raw : (raw?.data || []));

      if (resMethods.data && resMethods.data.success) {
        const actives = (resMethods.data.data || []).filter(m => m.is_active === 1);
        setPaymentMethods(actives);
        if (actives.length > 0) {
          setSelectedPaymentMethodId(actives[0].id);
        }
      }
    } catch (err) {
      console.error("Erreur chargement historique:", err);
      showToast("Erreur de chargement de l'historique des extras", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filtrage des données
  const filteredData = consommations.filter(item => {
    const matchSearch = 
      (item.nom_service && item.nom_service.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.client_nom && item.client_nom.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (item.id && item.id.toLowerCase().includes(searchTerm.toLowerCase()));

    const isSolde = item.statut_paiement === 'SOLDE' || item.reste_a_payer <= 0;

    if (filtreStatut === 'SOLDE') return matchSearch && isSolde;
    if (filtreStatut === 'IMPAYE') return matchSearch && !isSolde;
    return matchSearch;
  });

  // Trouver l'objet méthode de paiement sélectionné de manière sécurisée
  const currentMethodId = selectedPaymentMethodId || (paymentMethods.length > 0 ? paymentMethods[0].id : '');
  const selectedMethodObj = paymentMethods.find(m => String(m.id) === String(currentMethodId)) || paymentMethods[0];
  
  const isNotCashPayment = selectedMethodObj && 
    !['wallet', 'especes', 'cash', 'esp'].includes((selectedMethodObj.icone_name || '').toLowerCase()) &&
    !['esp', 'espece', 'espèces'].includes((selectedMethodObj.libelle || '').toLowerCase());

  // Soumission du paiement échelonné
  const handlePaiementSubmit = async (item) => {
    const montant = parseFloat(montantVerse) || 0;
    if (montant <= 0) {
      showToast("Veuillez entrer un montant valide", "error");
      return;
    }
    if (montant > item.reste_a_payer) {
      showToast(`Le montant ne peut pas dépasser le reste à payer (${item.reste_a_payer.toLocaleString()} F)`, "error");
      return;
    }
    if (isNotCashPayment && !referencePaiement.trim()) {
      showToast("Veuillez saisir la référence de paiement (obligatoire pour ce mode)", "error");
      return;
    }

    const finalMethodId = selectedPaymentMethodId || (paymentMethods[0] ? paymentMethods[0].id : null);
    if (!finalMethodId) {
      showToast("Veuillez sélectionner un mode de paiement valide", "error");
      return;
    }

    const finalMethodLibelle = selectedMethodObj ? selectedMethodObj.libelle : 'ESPECES';

    setIsSubmitting(true);
    try {
      await API.post(`/hebergement/extras/payer/${item.id}`, {
        montant_verse: montant,
        payment_method_id: finalMethodId,
        moyen_paiement: finalMethodLibelle,
        reference: referencePaiement.trim() || null,
        observation: `Paiement échelonné extra - ${item.nom_service}`
      });

      showToast("Paiement enregistré avec succès !");
      setExpandedRowId(null);
      setMontantVerse('');
      setReferencePaiement('');
      fetchData(); // Recharger les données
    } catch (err) {
      console.error("Erreur paiement extra:", err);
      showToast(err.response?.data?.error || "Erreur lors de l'enregistrement du paiement", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden', width: '100%' }}>
      <Sidebar />
      <main style={{ flex: 1, overflowY: 'auto', background: '#f8fafc', position: 'relative', display: 'flex', flexDirection: 'column' }}>
        
        {toast.show && (
          <div style={{...toastContainerStyle, background: toast.type === 'success' ? '#10b981' : '#ef4444'}}>
            {toast.type === 'success' ? <CheckCircle size={18}/> : <AlertTriangle size={18}/>}
            {toast.message}
          </div>
        )}

        <header style={headerStyle}>
          <div>
            <h1 style={{ margin: 0, fontSize: '20px', fontWeight: '900', display: 'flex', alignItems: 'center', gap: '10px', color: '#1e1b4b' }}>
              <FileText size={22} color="#7c3aed" /> HISTORIQUE & FACTURATION DES EXTRAS
            </h1>
            <div style={{ color: '#64748b', fontSize: '13px' }}>
              Suivi des consommations extras, statuts de paiement et règlements échelonnés
            </div>
          </div>

          {/* FILTRES PAR STATUT */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {['TOUS', 'SOLDE', 'IMPAYE'].map((st) => (
              <button 
                key={st}
                onClick={() => setFiltreStatut(st)}
                style={{
                  padding: '8px 14px', borderRadius: '8px', border: 'none', fontWeight: '800', fontSize: '12px', cursor: 'pointer',
                  background: filtreStatut === st ? '#7c3aed' : '#e2e8f0',
                  color: filtreStatut === st ? 'white' : '#475569',
                }}
              >
                {st === 'TOUS' ? 'Tous' : st === 'SOLDE' ? 'Soldés' : 'Impayés / Partiels'}
              </button>
            ))}
          </div>
        </header>

        <div style={{ padding: '30px 40px', flex: 1 }}>

          {/* BARRE DE RECHERCHE */}
          <div style={{ ...contentCardStyle, padding: '12px 20px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Search size={18} color="#64748b" />
            <input 
              type="text" 
              style={{ border: 'none', outline: 'none', width: '100%', fontSize: '14px', background: 'transparent' }} 
              placeholder="Rechercher par service, client ou référence..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div style={contentCardStyle}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>Prestation / Service</th>
                  <th style={thStyle}>Client / Bénéficiaire</th>
                  <th style={thStyle}>Montant TTC</th>
                  <th style={thStyle}>Reste à Payer</th>
                  <th style={thStyle}>Statut</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Chargement en cours...</td></tr>
                ) : filteredData.length === 0 ? (
                  <tr><td colSpan={7} style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Aucune consommation trouvée.</td></tr>
                ) : (
                  filteredData.map((item) => {
                    const isSolde = item.statut_paiement === 'SOLDE' || item.reste_a_payer <= 0;
                    const isExpanded = expandedRowId === item.id;

                    return (
                      <React.Fragment key={item.id}>
                        <tr style={{ borderBottom: '1px solid #f1f5f9', background: isExpanded ? '#f5f3ff' : 'transparent' }}>
                          <td style={tdStyle}>{new Date(item.date_consommation).toLocaleString('fr-FR')}</td>
                          <td style={{ ...tdStyle, fontWeight: '800', color: '#1e293b' }}>
                            {item.nom_service}
                            <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 'normal' }}>Ref: #{item.id}</div>
                          </td>
                          <td style={tdStyle}>{item.client_nom}</td>
                          <td style={{ ...tdStyle, fontWeight: '800' }}>{Math.round(item.montant_total).toLocaleString()} F</td>
                          <td style={{ ...tdStyle, fontWeight: '800', color: isSolde ? '#10b981' : '#d97706' }}>
                            {Math.round(item.reste_a_payer).toLocaleString()} F
                          </td>
                          <td style={tdStyle}>
                            <span style={{ 
                              padding: '4px 10px', borderRadius: '6px', fontSize: '10px', fontWeight: '900',
                              background: isSolde ? '#dcfce7' : '#fef3c7',
                              color: isSolde ? '#15803d' : '#b45309'
                            }}>
                              {isSolde ? 'SOLDÉ' : 'NON SOLDÉ'}
                            </span>
                          </td>
                          <td style={{ ...tdStyle, textAlign: 'right' }}>
                            {!isSolde && (
                              <button 
                                onClick={() => {
                                  setExpandedRowId(isExpanded ? null : item.id);
                                  setMontantVerse('');
                                  setReferencePaiement('');
                                  if (paymentMethods.length > 0) {
                                    setSelectedPaymentMethodId(paymentMethods[0].id);
                                  }
                                }}
                                style={{
                                  background: isExpanded ? '#64748b' : '#7c3aed', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px',
                                  fontSize: '12px', fontWeight: '800', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '5px'
                                }}
                              >
                                {isExpanded ? 'Fermer' : 'Payer'} {isExpanded ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}
                              </button>
                            )}
                          </td>
                        </tr>

                        {/* FORMULAIRE DE PAIEMENT DÉPLIÉ EN BAS DE LA LIGNE */}
                        {isExpanded && (
                          <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                            <td colSpan={7} style={{ padding: '20px' }}>
                              <div style={{ background: '#fff', padding: '20px', borderRadius: '10px', border: '1px solid #cbd5e1', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', maxWidth: '600px', margin: '0 auto' }}>
                                <div style={{ fontWeight: '900', fontSize: '13px', color: '#5b21b6', marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CreditCard size={16} /> ENCAISSEMENT ÉCHELONNÉ — Restant dû : <span style={{ color: '#d97706' }}>{Math.round(item.reste_a_payer).toLocaleString()} F</span>
                                </div>

                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                                  <div>
                                    <label style={labelStyle}>MODE DE RÈGLEMENT</label>
                                    <select 
                                      style={inputStyle}
                                      value={currentMethodId}
                                      onChange={(e) => setSelectedPaymentMethodId(e.target.value)}
                                    >
                                      {paymentMethods.map(m => (
                                        <option key={m.id} value={m.id}>{m.libelle}</option>
                                      ))}
                                    </select>
                                  </div>
                                  <div>
                                    <label style={labelStyle}>MONTANT VERSÉ (F CFA) *</label>
                                    <input 
                                      type="number" 
                                      style={{ ...inputStyle, fontWeight: 'bold' }} 
                                      placeholder="0"
                                      max={item.reste_a_payer}
                                      value={montantVerse}
                                      onChange={(e) => setMontantVerse(e.target.value)}
                                    />
                                  </div>
                                </div>

                                {/* CHAMP DE RÉFÉRENCE CONDITIONNEL SI NON ESPÈCES */}
                                {isNotCashPayment && (
                                  <div style={{ marginBottom: '15px', background: '#fef3c7', padding: '10px', borderRadius: '6px', border: '1px solid #f59e0b' }}>
                                    <label style={{ fontSize: '10px', fontWeight: '900', color: '#b45309' }}>RÉFÉRENCE DE PAIEMENT / TRANSACTION *</label>
                                    <input 
                                      type="text" 
                                      style={{ ...inputStyle, background: '#fff', marginTop: '4px' }}
                                      placeholder="Ex: N° de transaction, chèque..."
                                      value={referencePaiement}
                                      onChange={(e) => setReferencePaiement(e.target.value)}
                                    />
                                  </div>
                                )}

                                {parseFloat(montantVerse) > 0 && (
                                  <div style={{ marginBottom: '15px', fontSize: '12px', fontWeight: '800', color: parseFloat(montantVerse) >= item.reste_a_payer ? '#059669' : '#d97706' }}>
                                    {parseFloat(montantVerse) >= item.reste_a_payer 
                                      ? '✅ Ce versement soldera entièrement la facture.' 
                                      : `⚠️ Nouveau reste après ce versement : ${Math.round(item.reste_a_payer - parseFloat(montantVerse)).toLocaleString()} F (Paiement Partiel)`}
                                  </div>
                                )}

                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                                  <button 
                                    type="button"
                                    onClick={() => setExpandedRowId(null)}
                                    style={{ padding: '8px 14px', borderRadius: '6px', border: '1px solid #cbd5e1', background: '#fff', fontWeight: '800', cursor: 'pointer', fontSize: '12px' }}
                                  >
                                    Annuler
                                  </button>
                                  <button 
                                    type="button"
                                    onClick={() => handlePaiementSubmit(item)}
                                    disabled={isSubmitting}
                                    style={{ padding: '8px 18px', borderRadius: '6px', border: 'none', background: '#7c3aed', color: 'white', fontWeight: '800', cursor: 'pointer', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '5px' }}
                                  >
                                    <CheckCircle2 size={14} /> {isSubmitting ? 'Validation...' : 'Valider le Paiement'}
                                  </button>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

        </div>
      </main>

      <style>{`@keyframes slideInToast { from { transform: translate(-50%, -20px); opacity: 0; } to { transform: translate(-50%, 0); opacity: 1; } }`}</style>
    </div>
  );
};

// --- STYLES ---
const headerStyle = { background: 'white', padding: '20px 40px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const contentCardStyle = { background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)', overflow: 'hidden' };
const thStyle = { padding: '15px 20px', fontSize: '11px', color: '#64748b', textTransform: 'uppercase', fontWeight: '800', textAlign: 'left' };
const tdStyle = { padding: '12px 20px', fontSize: '13px', color: '#334155' };
const inputStyle = { width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid #cbd5e1', outline: 'none', fontSize: '13px', marginTop: '4px', background: '#fff', boxSizing: 'border-box' };
const labelStyle = { fontSize: '10px', fontWeight: '900', color: '#475569', textTransform: 'uppercase' };
const toastContainerStyle = { position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)', zIndex: 9999, color: 'white', padding: '12px 24px', borderRadius: '12px', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)', display: 'flex', alignItems: 'center', gap: '10px', fontWeight: '800', animation: 'slideInToast 0.3s ease-out' };

export default HistoriqueExtras;